import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import './Countries.css';
import InputBoxLarge from '../../../components/common/InputBox/InputBoxLarge';
import InputBoxSmall from '../../../components/common/InputBox/InputBoxSmall';
import SearchSelectBoxDrown from '../../../components/common/SelectBox/searchSelect';
import EditUploadBoxCountry from '../../../components/common/InputBox/EditUpload';
import SearchSelectBox from '../../../components/common/SelectBox/SelectBoxWithSearch';
import SearchSelectTimezone from '../../../components/common/SelectBox/SelectBoxWithSearchTimezone';
import ErrorNotification from '../../../components/common/AlertBox/ErrorNotification';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';
import authenticatedAxios from '../../../authenticatedAxios';
import { baseurl } from '../../../api';
import { isRequired, isTextOnly, isValidCountryCode } from '../../../validation';
import useRefreshStore from '../../../store/useRefreshStore';

const CountryEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { triggerRefresh } = useRefreshStore();

  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [activePlanOptions, setActivePlanOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [existingFlag, setExistingFlag] = useState(null);

  const [form, setForm] = useState({
    name: '',
    iso_code: '',
    country_code: '',
    currency: '',
    country_flag: null,
    plans: [],
    timezone: [],
  });

  const fetchCurrencyOptions = async () => {
    try {
      const response = await authenticatedAxios.get(`${baseurl}currency/active/`);
      if (response.data && response.data.response_code === 1) {
        const options = (response.data.data || []).map((currency) => ({
          label: currency.name ? `${currency.name}${currency.symbol ? ` (${currency.symbol})` : ''}` : currency.symbol,
          value: currency.id,
        }));
        setCurrencyOptions(options);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  const fetchAllActivePlans = async () => {
    try {
      const response = await authenticatedAxios.get(`${baseurl}plans/active/`);
      if (response.data && response.data.response_code === 1) {
        const options = (response.data.data || []).map((plan) => ({
          label: plan.plan_name || plan.display_name,
          value: plan.id,
          id: plan.id,
        }));
        setActivePlanOptions(options);
      }
    } catch (error) {
      console.error('Error fetching active plans:', error);
    }
  };

  const fetchTimezoneOptions = async () => {
    try {
      const { data } = await authenticatedAxios.get(`${baseurl}localization/timezones/`);
      if (data && data.response_code === 1) {
        const options = (data.data || []).map((tz) => ({
          label: `${tz.name} (${tz.timezone})`,
          value: tz.id,
          id: tz.id,
        }));
        setTimezoneOptions(options);
      }
    } catch (err) {
      console.error('Error fetching timezones:', err);
    }
  };

  const fetchCountryDetail = async () => {
    try {
      const response = await authenticatedAxios.get(`${baseurl}localization/country/?id=${id}`);
      if (response.data && response.data.data) {
        const country = response.data.data;
        const flagUrl = country.country_flag && typeof country.country_flag === 'string'
          ? `${baseurl.replace(/\/$/, '')}${country.country_flag.startsWith('/') ? '' : '/'}${country.country_flag}`
          : null;

        setExistingFlag(flagUrl);

        const mappedPlans = (country.plans || []).map((plan) => ({
          id: plan.id,
          value: plan.id,
          label: plan.plan_name || plan.display_name,
        }));

        const mappedTimezones = (country.timezones || []).map((tz) => ({
          id: tz.id,
          value: tz.id,
          label: `${tz.name} (${tz.timezone})`,
        }));

        setForm({
          name: country.name || '',
          iso_code: country.iso_code || '',
          country_code: country.country_code || '',
          currency: country.currency?.id || country.currency || '',
          country_flag: null,
          plans: mappedPlans,
          timezone: mappedTimezones,
        });
      } else {
        setErrors({ general: 'Country not found.' });
      }
    } catch (error) {
      console.error('Error fetching country:', error);
      if (error?.response?.data?.error_code === 1001) {
        navigate('/permission-denied');
      } else {
        setErrors({ general: 'Failed to load country details.' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrencyOptions();
    fetchAllActivePlans();
    fetchTimezoneOptions();
    if (id) {
      fetchCountryDetail();
    }
  }, [id]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.name || !form.name.trim()) {
      newErrors.name = ['Country Name is required'];
    } else {
      const textOnly = isTextOnly(form.name);
      if (textOnly !== true) {
        newErrors.name = [textOnly];
      }
    }

    if (!form.iso_code || !form.iso_code.trim()) {
      newErrors.iso_code = ['ISO Code is required'];
    }

    if (!form.country_code || !form.country_code.trim()) {
      newErrors.country_code = ['Country Code is required'];
    } else {
      const codeCheck = isValidCountryCode(form.country_code);
      if (codeCheck !== true) {
        newErrors.country_code = [codeCheck];
      }
    }

    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const formData = new FormData();
    formData.append('name', form.name.trim());
    formData.append('iso_code', form.iso_code.trim());
    formData.append('country_code', form.country_code.trim());
    if (form.currency) {
      formData.append('currency', form.currency);
    }

    if (form.country_flag instanceof File) {
      formData.append('country_flag', form.country_flag);
    }

    if (Array.isArray(form.plans)) {
      form.plans.forEach((plan) => {
        formData.append('plans', plan.value || plan.id);
      });
    }

    if (Array.isArray(form.timezone)) {
      form.timezone.forEach((tz) => {
        formData.append('timezones', tz.value || tz.id);
      });
    }

    try {
      const response = await authenticatedAxios.put(
        `${baseurl}localization/country/?id=${id}`,
        formData
      );

      if (response.data && response.data.response_code === 1) {
        triggerRefresh('Countries');
        SuccessAlert('Country updated successfully');
        navigate(`/countries/detail/${id}`);
      } else {
        const msg = response.data?.message;
        if (msg && typeof msg === 'object') {
          setErrors(msg);
        } else {
          setErrors({ general: msg || 'Failed to update country.' });
        }
      }
    } catch (error) {
      console.error('Error updating country:', error);
      if (error.response?.data?.message) {
        const msg = error.response.data.message;
        if (typeof msg === 'object') {
          setErrors(msg);
        } else {
          setErrors({ general: msg });
        }
      } else {
        setErrors({ general: 'Failed to update country. Please try again.' });
      }
    }
  };

  if (loading) {
    return (
      <div className="whole-container">
        <p className="common-text fts-14 ftw-4" style={{ padding: '20px' }}>Loading country details...</p>
      </div>
    );
  }

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader
          pageName="Localization / Countries /"
          nestedpageName="Edit"
          linkTo="/countries"
        />
        <div className="export-container">
          <ExportLabel />
        </div>
      </div>

      <div className="page-container">
        <ErrorNotification errors={errors} marginBottom="16px" />

        {/* Country Details Card */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Country Details</span>
            <button
              className="popup-add-button common-text white fts-15 ftw-5"
              onClick={handleSubmit}
            >
              Save Country
            </button>
          </div>

          <div className="card-body">
            <InputBoxLarge
              label="Country Name"
              defaultValue={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              isRequired
              error={errors.name}
            />

            <div className="row-2">
              <InputBoxSmall
                label="ISO Code"
                defaultValue={form.iso_code}
                onChange={(e) => handleChange('iso_code', e.target.value)}
                isRequired
                error={errors.iso_code}
              />
              <InputBoxSmall
                label="Country Code"
                defaultValue={form.country_code}
                onChange={(e) => handleChange('country_code', e.target.value)}
                isRequired
                error={errors.country_code}
              />
            </div>

            <SearchSelectBoxDrown
              label="Currency"
              options={currencyOptions}
              defaultValue={form.currency}
              onChange={(e) => handleChange('currency', e.target.value)}
              error={errors.currency}
            />

            <EditUploadBoxCountry
              label="Upload Flag"
              existingImage={existingFlag}
              onFileChange={(file) => handleChange('country_flag', file)}
            />
          </div>
        </div>

        {/* Active Plans Card */}
        <div className="features-card">
          <SearchSelectBox
            label="Choose Active Plans"
            allPlans={activePlanOptions}
            defaultSelectedOptions={form.plans}
            onChange={(selected) => handleChange('plans', selected)}
            error={errors.plans}
          />
        </div>

        {/* Timezones Card */}
        <div className="features-card">
          <SearchSelectTimezone
            label="Choose Timezones"
            allTimezones={timezoneOptions}
            defaultSelectedOptions={form.timezone}
            onChange={(selected) => handleChange('timezone', selected)}
            error={errors.timezone}
          />
        </div>
      </div>
    </div>
  );
};

export default CountryEdit;
